// src/components/.../CompanyDialog.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogTitle,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Tabs,
  Tab,
  Box,
  Typography,
  useMediaQuery,
  Grid,
  Divider,
  Chip,
  Switch,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMutation, useQuery } from "react-query";
import client from "../../../API";
import useAlertDialog from "../../../Components/useAlertDialog";
import AlertDialog from "../../../Components/AlertDialog";
import AdminCompanyTeams from "./userCompanyTools/adminteams";
import AdminCompanyFiles from "./userCompanyTools/companyTeamFiles";

// -------- Helpers --------
const BOTTOM_NAV_HEIGHT = 86;

// Default banking information
const DEFAULT_BANKING_INFO = {
  bankName: "Chase Business Banking",
  accountHolder: "Blitz Systems Corp",
  accountNumber: "730966937",
  routingNumber: "071000013",
  swiftCode: "CHASUS33",
  bankAddress: "270 Park Ave., New York, NY 10017",
};

function parseSeatsString(seatsStr) {
  if (!seatsStr.trim()) return [];
  return seatsStr.split(",").map((s) => s.trim()).filter(Boolean);
}
function formatNumber(n) {
  if (n === null || n === undefined) return "";
  return n.toString();
}

// Helper to format Date object to YYYY-MM-DD
function formatDateForInput(date) {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-tabpanel-${index}`}
      aria-labelledby={`company-tab-${index}`}
      style={{ height: "100%" }}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

export default function CompanyDialog({ open, onClose, info }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { dialogState, openDialog } = useAlertDialog();

  // Tabs
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (_e, newValue) => setTabIndex(newValue);

  // Edit state (existing)
  const [companyName, setCompanyName] = useState("");
  const [balance, setBalance] = useState("");
  const [accountStatus, setAccountStatus] = useState("Free");
  const [seats, setSeats] = useState("");
  const [phphref, setPhphref] = useState("");
  const [nominalInterestRate, setNominalInterestRate] = useState("");
  const [spendingPowerLimit, setSpendingPowerLimit] = useState("");
  const [vendorSystem, setVendorSystem] = useState(false);
  const [vendorSystemName, setVendorSystemName] = useState("");
  const [vendorPaymentTerm, setVendorPaymentTerm] = useState("30");
  const [blitzVendorId, setBlitzVendorId] = useState("");
  const [supervisorId, setSupervisorId] = useState("");

  // AP Contact Information States
  const [apEmail, setApEmail] = useState("");
  const [apContactName, setApContactName] = useState("");
  const [apPhone, setApPhone] = useState("");
  const [apInfoVerified, setApInfoVerified] = useState(false);

  // Banking Information States
  const [bankName, setBankName] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [swiftCode, setSwiftCode] = useState("");
  const [bankAddress, setBankAddress] = useState("");
  const [beneficiaryAddress, setBeneficiaryAddress] = useState("");

  // BlitzPay-specific states
  const [blitzpayAccount, setBlitzpayAccount] = useState(false);
  const [msaInitiated, setMsaInitiated] = useState(false);
  const [msaInitiatedBy, setMsaInitiatedBy] = useState("");
  const [msaInitiatedDate, setMsaInitiatedDate] = useState("");
  const [starterLineInitiated, setStarterLineInitiated] = useState(false);
  const [starterLineStartDate, setStarterLineStartDate] = useState("");
  const [subAccount, setSubAccount] = useState("");

  // NEW: Collabs Access state
  const [collabsAccess, setCollabsAccess] = useState(false);

  // Supervisors
  // useQuery("supervisors", () => client.users.list(), { enabled: open });

  // Helper function to use default banking info
  const handleUseDefaultBanking = () => {
    setBankName(DEFAULT_BANKING_INFO.bankName);
    setAccountHolder(DEFAULT_BANKING_INFO.accountHolder);
    setAccountNumber(DEFAULT_BANKING_INFO.accountNumber);
    setRoutingNumber(DEFAULT_BANKING_INFO.routingNumber);
    setSwiftCode(DEFAULT_BANKING_INFO.swiftCode);
    setBankAddress(DEFAULT_BANKING_INFO.bankAddress);
  };

  // Prefill on open
  useEffect(() => {
    if (open && info) {
      setCompanyName(info.name || "");
      setBalance(info.balance != null ? formatNumber(info.balance) : "");
      setAccountStatus(info.account_status || "Free");
      setSeats(info.seats && Array.isArray(info.seats) ? info.seats.join(",") : "");
      setPhphref(info.phphref || "");
      setNominalInterestRate(
        info.nominal_interest_rate != null ? formatNumber(info.nominal_interest_rate) : "10"
      );
      setSpendingPowerLimit(
        info.spending_power_limit != null ? formatNumber(info.spending_power_limit) : "0"
      );
      setVendorSystem(!!info.vendor_system);
      setVendorSystemName(info.vendor_system_name || "");
      setVendorPaymentTerm(
        info.vendor_payment_term != null ? String(info.vendor_payment_term) : "30"
      );
      setBlitzVendorId(info.blitz_vendor_id || "");
      setSupervisorId(info.supervisor_id ? String(info.supervisor_id) : "");

      // Prefill AP contact fields
      setApEmail(info.ap_email || "");
      setApContactName(info.ap_contact_name || "");
      setApPhone(info.ap_phone || "");
      setApInfoVerified(!!info.ap_info_verified);

      // Parse banking info from ap_routing_details JSON
      if (info.ap_routing_details && typeof info.ap_routing_details === 'object') {
        setBankName(info.ap_routing_details.bankName || "");
        setAccountHolder(info.ap_routing_details.accountHolder || "");
        setAccountNumber(info.ap_routing_details.accountNumber || "");
        setRoutingNumber(info.ap_routing_details.routingNumber || "");
        setSwiftCode(info.ap_routing_details.swiftCode || "");
        setBankAddress(info.ap_routing_details.bankAddress || "");
        setBeneficiaryAddress(info.ap_routing_details.beneficiaryAddress || "");
      } else {
        setBankName("");
        setAccountHolder("");
        setAccountNumber("");
        setRoutingNumber("");
        setSwiftCode("");
        setBankAddress("");
        setBeneficiaryAddress("");
      }

      // Prefill BlitzPay fields
      setBlitzpayAccount(!!info.blitzpay_account);

      // Parse MSA initiated
      if (info.msa_initiated && typeof info.msa_initiated === 'object') {
        setMsaInitiated(!!info.msa_initiated.initiated);
        setMsaInitiatedBy(info.msa_initiated.initiated_by || "");
        setMsaInitiatedDate(formatDateForInput(info.msa_initiated.initiated_date));
      } else {
        setMsaInitiated(false);
        setMsaInitiatedBy("");
        setMsaInitiatedDate("");
      }

      // Parse Starter Line initiated
      if (info.starter_line_initiated && typeof info.starter_line_initiated === 'object') {
        setStarterLineInitiated(!!info.starter_line_initiated.initiated);
        setStarterLineStartDate(formatDateForInput(info.starter_line_initiated.start_date));
      } else {
        setStarterLineInitiated(false);
        setStarterLineStartDate("");
      }

      setSubAccount(info.sub_account || "");

      // NEW: Prefill Collabs Access
      setCollabsAccess(!!info.collabs_access);

    } else if (open && !info) {
      // Reset all fields
      setCompanyName("");
      setBalance("");
      setAccountStatus("Free");
      setSeats("");
      setPhphref("");
      setNominalInterestRate("10");
      setSpendingPowerLimit("0");
      setVendorSystem(false);
      setVendorSystemName("");
      setVendorPaymentTerm("30");
      setBlitzVendorId("");
      setSupervisorId("");
      setApEmail("");
      setApContactName("");
      setApPhone("");
      setApInfoVerified(false);
      setBankName("");
      setAccountHolder("");
      setAccountNumber("");
      setRoutingNumber("");
      setSwiftCode("");
      setBankAddress("");
      setBeneficiaryAddress("");
      setBlitzpayAccount(false);
      setMsaInitiated(false);
      setMsaInitiatedBy("");
      setMsaInitiatedDate("");
      setStarterLineInitiated(false);
      setStarterLineStartDate("");
      setSubAccount("");
      setCollabsAccess(false);
    }
  }, [open, info]);

  // Mutations
  const { mutate: createCompany, isLoading: createLoading } = useMutation(
    client.companies.create,
    {
      onSuccess: () => onClose(true),
      onError: (e) => openDialog("Error", e?.response?.data?.error || e.message),
    }
  );
  const { mutate: editCompany, isLoading: editLoading } = useMutation(
    client.companies.edit,
    {
      onSuccess: () => onClose(true),
      onError: (e) => openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  const handleSubmitEdit = () => {
    if (!companyName.trim()) {
      openDialog("Error", "Company name is required.");
      return;
    }

    // Build banking info object from individual fields
    const bankingInfo = {};
    if (bankName.trim()) bankingInfo.bankName = bankName.trim();
    if (accountHolder.trim()) bankingInfo.accountHolder = accountHolder.trim();
    if (accountNumber.trim()) bankingInfo.accountNumber = accountNumber.trim();
    if (routingNumber.trim()) bankingInfo.routingNumber = routingNumber.trim();
    if (swiftCode.trim()) bankingInfo.swiftCode = swiftCode.trim();
    if (bankAddress.trim()) bankingInfo.bankAddress = bankAddress.trim();
    if (beneficiaryAddress.trim()) bankingInfo.beneficiaryAddress = beneficiaryAddress.trim();

    // Build MSA initiated object
    const msaInitiatedObj = {
      initiated: msaInitiated,
      initiated_by: msaInitiatedBy.trim() || null,
      initiated_date: msaInitiatedDate || null,
    };

    // Build Starter Line initiated object
    const starterLineInitiatedObj = {
      initiated: starterLineInitiated,
      start_date: starterLineStartDate || null,
    };

    const data = {
      company_name: companyName.trim(),
      balance: parseFloat(balance) || 0,
      account_status: accountStatus,
      seats: parseSeatsString(seats),
      phphref,
      nominal_interest_rate: parseFloat(nominalInterestRate) || 0,
      spending_power_limit: parseFloat(spendingPowerLimit) || 0,
      vendor_system: vendorSystem,
      vendor_system_name: vendorSystemName.trim() || null,
      vendor_payment_term: parseInt(vendorPaymentTerm, 10) || 30,
      blitz_vendor_id: blitzVendorId.trim() || null,
      supervisor_id: supervisorId ? parseInt(supervisorId, 10) : null,
      ap_email: apEmail.trim() || null,
      ap_contact_name: apContactName.trim() || null,
      ap_phone: apPhone.trim() || null,
      ap_routing_details: Object.keys(bankingInfo).length > 0 ? bankingInfo : null,
      ap_info_verified: apInfoVerified,
      // BlitzPay fields
      blitzpay_account: blitzpayAccount,
      msa_initiated: msaInitiatedObj,
      starter_line_initiated: starterLineInitiatedObj,
      sub_account: subAccount.trim() || null,
      // NEW: Collabs Access
      collabs_access: collabsAccess,
    };

    if (info?.id) editCompany({ companyId: info.id, params: data });
    else createCompany(data);
  };

  // Invoices (existing)
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("");
  const {
    data: invoiceData = [],
    isLoading: invoicesLoading,
    refetch: refetchInvoices,
  } = useQuery(
    ["companyInvoices", info?.id, invoiceStatusFilter],
    () => client.companies.invoice(info.id, invoiceStatusFilter),
    { enabled: tabIndex === 1 && !!info?.id }
  );
  const handleInvoiceStatusChange = (e) => {
    setInvoiceStatusFilter(e.target.value);
    refetchInvoices();
  };

  // Spending power ledger (existing)
  const {
    data: spendingLedgerData = [],
    isLoading: ledgerLoading,
    refetch: refetchLedger,
  } = useQuery(
    ["companySpendingLedger", info?.id],
    () => client.creditLedger.fetch(info.id),
    { enabled: tabIndex === 2 && !!info?.id }
  );

  const [operationType, setOperationType] = useState("init");
  const [requestedSpendingPower, setRequestedSpendingPower] = useState("");
  const [reason, setReason] = useState("");

  const { mutate: createSpendingOp, isLoading: creatingSpendingOp } = useMutation(
    (payload) => client.creditLedger.postRequest(payload),
    {
      onSuccess: () => {
        refetchLedger();
        openDialog("Success", "Operation created successfully.");
        setOperationType("init");
        setRequestedSpendingPower("");
        setReason("");
      },
      onError: (e) => openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  const handleCreateOperation = () => {
    if (!info?.id) {
      openDialog("Error", "No company selected.");
      return;
    }
    const newLine = parseFloat(requestedSpendingPower);
    if (isNaN(newLine)) {
      openDialog("Error", "Please enter a valid number.");
      return;
    }
    createSpendingOp({
      company_id: info.id,
      operation_type: operationType,
      requested_credit_line: newLine,
      requested_interest_rate: 0,
      reason: reason.trim() || "Manual operation by admin",
    });
  };

  // Approvals (existing)
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [overrideSpendingPower, setOverrideSpendingPower] = useState("");
  const [overrideInterestRate, setOverrideInterestRate] = useState("");
  const [adminReason, setAdminReason] = useState("");

  const { mutate: approveOrDeny, isLoading: isApproving } = useMutation(
    (payload) => client.creditLedger.approve(payload),
    {
      onSuccess: () => {
        refetchLedger();
        openDialog("Success", "Request processed successfully.");
      },
      onError: (e) => openDialog("Error", e?.response?.data?.error || e.message),
    }
  );
  const handleOpenApproveDialog = (req) => {
    setSelectedRequest(req);
    setOverrideSpendingPower(String(req.new_limit || req.spending_power_limit));
    setOverrideInterestRate(String(req.new_interest_rate));
    setAdminReason("");
    setApproveDialogOpen(true);
  };
  const handleCloseApproveDialog = () => {
    setApproveDialogOpen(false);
    setSelectedRequest(null);
  };
  const handleApprove = () => {
    if (!selectedRequest) return;
    approveOrDeny({
      request_id: selectedRequest.id,
      approve: true,
      new_credit_line: parseFloat(overrideSpendingPower),
      new_interest_rate: parseFloat(overrideInterestRate),
      reason: adminReason || "Approved by admin",
    });
    handleCloseApproveDialog();
  };
  const handleDeny = (req) =>
    approveOrDeny({ request_id: req.id, approve: false, reason: "Denied by admin" });

  // Safe-area padding values
  const topPad = isMobile ? "calc(env(safe-area-inset-top,0px) + 8px)" : 0;
  const bottomPad = isMobile
    ? `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom,0px) + 8px)`
    : 0;

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          ...(isMobile && { height: "100%" }),
          pt: topPad,
          pb: bottomPad,
        },
      }}
    >
      <AlertDialog alertState={dialogState} />

      <DialogTitle
        sx={{
          pr: 2,
          pt: 0,
          pb: 1,
        }}
      >
        {info?.id ? "Edit Company" : "Create Company"}
        {info?.id && (
          <Box component="span" sx={{ ml: 2 }}>
            {blitzpayAccount && (
              <Chip 
                label="BlitzPay" 
                color="primary" 
                size="small" 
                sx={{ mr: 1 }}
              />
            )}
            {collabsAccess && (
              <Chip 
                label="Collabs Access" 
                color="success" 
                size="small" 
              />
            )}
          </Box>
        )}
      </DialogTitle>

      {/* Tabs bar */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          position: isMobile ? "sticky" : "static",
          top: 0,
          zIndex: 10,
          backgroundColor: "background.paper",
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Edit" />
          <Tab label="Invoices" />
          <Tab label="Spending Power History" />
          <Tab label="Teams" />
          <Tab label="Files" />
        </Tabs>
      </Box>

      {/* Scrollable content area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          {/* EDIT TAB */}
          <TabPanel value={tabIndex} index={0}>
            {/* Access Toggles Section */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* BlitzPay Account Toggle */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: blitzpayAccount ? "primary.50" : "grey.50", borderRadius: 2, height: "100%" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={blitzpayAccount}
                        onChange={(e) => setBlitzpayAccount(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          BlitzPay Account
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Enable BlitzPay features for this company
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Grid>

              {/* NEW: Collabs Access Toggle */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: collabsAccess ? "success.50" : "grey.50", borderRadius: 2, height: "100%" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={collabsAccess}
                        onChange={(e) => setCollabsAccess(e.target.checked)}
                        color="success"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Collabs Access
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Grant access to Blitz collaboration features
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Show alerts if features are enabled */}
            {(blitzpayAccount || collabsAccess) && (
              <Box sx={{ mb: 3 }}>
                {blitzpayAccount && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    This company has BlitzPay enabled. Additional fields and features are available below.
                  </Alert>
                )}
                {collabsAccess && (
                  <Alert severity="success" sx={{ mb: 1 }}>
                    This company has Collabs Access enabled. They can use Blitz collaboration features.
                  </Alert>
                )}
              </Box>
            )}

            <TextField
              label="Company Name"
              fullWidth
              margin="normal"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
            <TextField
              label="Balance"
              type="number"
              fullWidth
              margin="normal"
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Account Status</InputLabel>
              <Select
                label="Account Status"
                value={accountStatus}
                onChange={(e) => setAccountStatus(e.target.value)}
              >
                <MenuItem value="Free">Free</MenuItem>
                <MenuItem value="Agency">Agency</MenuItem>
                <MenuItem value="AlphaFree">AlphaFree</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Seats (comma-separated)"
              fullWidth
              margin="normal"
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
            />
            <TextField
              label="PHP href"
              fullWidth
              margin="normal"
              value={phphref}
              onChange={(e) => setPhphref(e.target.value)}
            />
            <TextField
              label="Nominal Interest Rate (%)"
              type="number"
              fullWidth
              margin="normal"
              value={nominalInterestRate}
              onChange={(e) => setNominalInterestRate(e.target.value)}
            />
            <TextField
              label="Spending Power Limit"
              type="number"
              fullWidth
              margin="normal"
              value={spendingPowerLimit}
              onChange={(e) => setSpendingPowerLimit(e.target.value)}
              helperText="Unified spending power limit for this company"
            />
            {/* BlitzPay-specific fields */}
            {blitzpayAccount && (
              <>
                <Divider sx={{ my: 3 }}>
                  <Chip label="BlitzPay Configuration" color="primary" />
                </Divider>

                <TextField
                  label="Sub-Account"
                  fullWidth
                  value={subAccount}
                  onChange={(e) => setSubAccount(e.target.value)}
                  helperText="Parent company or sub-account identifier (if applicable)"
                />

                {/* MSA Initiated Section */}
                <Box sx={{ mt: 3, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={msaInitiated}
                        onChange={(e) => setMsaInitiated(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        MSA Initiated
                      </Typography>
                    }
                  />
                  {msaInitiated && (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Initiated By</InputLabel>
                          <Select
                            label="Initiated By"
                            value={msaInitiatedBy}
                            onChange={(e) => setMsaInitiatedBy(e.target.value)}
                          >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="Legal">Legal</MenuItem>
                            <MenuItem value="CFO">CFO</MenuItem>
                            <MenuItem value="CEO">CEO</MenuItem>
                            <MenuItem value="Account Manager">Account Manager</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Initiated Date"
                          type="date"
                          fullWidth
                          value={msaInitiatedDate}
                          onChange={(e) => setMsaInitiatedDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>

                {/* Starter Line Initiated Section */}
                <Box sx={{ mt: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={starterLineInitiated}
                        onChange={(e) => setStarterLineInitiated(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Starter Line Initiated
                      </Typography>
                    }
                  />
                  {starterLineInitiated && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        label="Start Date"
                        type="date"
                        fullWidth
                        value={starterLineStartDate}
                        onChange={(e) => setStarterLineStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  )}
                </Box>
              </>
            )}

            <Divider sx={{ my: 3 }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={vendorSystem}
                  onChange={(e) => setVendorSystem(e.target.checked)}
                />
              }
              label="Vendor System?"
            />
            {vendorSystem && (
              <TextField
                label="Vendor System Name"
                fullWidth
                margin="normal"
                value={vendorSystemName}
                onChange={(e) => setVendorSystemName(e.target.value)}
              />
            )}
            <TextField
              label="Vendor Payment Term (days)"
              type="number"
              fullWidth
              margin="normal"
              value={vendorPaymentTerm}
              onChange={(e) => setVendorPaymentTerm(e.target.value)}
            />
            <TextField
              label="Blitz Vendor ID"
              fullWidth
              margin="normal"
              value={blitzVendorId}
              onChange={(e) => setBlitzVendorId(e.target.value)}
            />
            <TextField
              label="Account Supervisor ID"
              type="number"
              fullWidth
              margin="normal"
              value={supervisorId}
              onChange={(e) => setSupervisorId(e.target.value)}
              helperText="Enter the supervisor's user ID"
            />

            <Divider sx={{ my: 3 }} />

            {/* AP INFORMATION SECTION */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="h6">
                AP Information
              </Typography>
            </Box>

            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
              Contact Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="AP Email"
                  fullWidth
                  value={apEmail}
                  onChange={(e) => setApEmail(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="AP Contact Name"
                  fullWidth
                  value={apContactName}
                  onChange={(e) => setApContactName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="AP Phone"
                  fullWidth
                  value={apPhone}
                  onChange={(e) => setApPhone(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={apInfoVerified}
                      onChange={(e) => setApInfoVerified(e.target.checked)}
                    />
                  }
                  label="AP Info Verified"
                  sx={{ mt: 1 }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* BANKING INFORMATION SECTION */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Banking Information
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleUseDefaultBanking}
              >
                Use Default
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Bank Name"
                  fullWidth
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  helperText="e.g., Chase Business Banking"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Account Holder"
                  fullWidth
                  value={accountHolder}
                  onChange={(e) => setAccountHolder(e.target.value)}
                  helperText="e.g., Blitz Systems Corp"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Account Number"
                  fullWidth
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Routing Number"
                  fullWidth
                  value={routingNumber}
                  onChange={(e) => setRoutingNumber(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="SWIFT Code"
                  fullWidth
                  value={swiftCode}
                  onChange={(e) => setSwiftCode(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Bank Address"
                  fullWidth
                  value={bankAddress}
                  onChange={(e) => setBankAddress(e.target.value)}
                  helperText="e.g., 270 Park Ave., New York, NY 10017"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Beneficiary Address"
                  fullWidth
                  value={beneficiaryAddress}
                  onChange={(e) => setBeneficiaryAddress(e.target.value)}
                  helperText="Company's address for wire transfers"
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* INVOICES TAB */}
          <TabPanel value={tabIndex} index={1}>
            {info?.id ? (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2, flexWrap: "wrap" }}>
                  <Typography sx={{ mr: 2 }}>Filter by Status:</Typography>
                  <Select
                    size="small"
                    value={invoiceStatusFilter}
                    onChange={handleInvoiceStatusChange}
                    displayEmpty
                    sx={{ minWidth: 160 }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                  </Select>
                  {invoicesLoading && <CircularProgress size={24} sx={{ ml: 2 }} />}
                </Box>
                {invoiceData.length === 0 && !invoicesLoading && (
                  <Typography>No invoices found.</Typography>
                )}
                {invoiceData.map((inv) => (
                  <Box
                    key={inv.id}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2,
                      mb: 2,
                    }}
                  >
                    <Typography>
                      <strong>Invoice ID:</strong> {inv.id}
                    </Typography>
                    <Typography>
                      <strong>PO #:</strong> {inv.po_number}
                    </Typography>
                    <Typography>
                      <strong>Status:</strong> {inv.status}
                    </Typography>
                    <Typography>
                      <strong>Amount Due:</strong> {inv.amount_due}
                    </Typography>
                    <Typography>
                      <strong>Days Late:</strong> {inv.days_late}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography>You need to create/save the company first to view invoices.</Typography>
            )}
          </TabPanel>

          {/* SPENDING POWER HISTORY TAB */}
          <TabPanel value={tabIndex} index={2}>
            {info?.id ? (
              <Box>
                {ledgerLoading && <CircularProgress size={24} />}
                {!ledgerLoading && spendingLedgerData.length === 0 && (
                  <Typography>No spending power ledger entries found.</Typography>
                )}
                {spendingLedgerData.map((entry) => (
                  <Box
                    key={entry.id}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2,
                      mb: 2,
                    }}
                  >
                    <Typography>
                      <strong>Operation:</strong> {entry.operation_type}
                    </Typography>
                    <Typography>
                      <strong>Change Amount:</strong> {entry.change_amount}
                    </Typography>
                    <Typography>
                      <strong>New Spending Power:</strong> {entry.new_limit || entry.spending_power_limit}
                    </Typography>
                    <Typography>
                      <strong>Interest Rate:</strong> {entry.new_interest_rate}
                    </Typography>
                    <Typography>
                      <strong>Reason:</strong> {entry.reason}
                    </Typography>
                    <Typography>
                      <strong>Created At:</strong>{" "}
                      {new Date(entry.created_at).toLocaleString()}
                    </Typography>
                    {entry.operation_type === "request" && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="contained"
                          color="success"
                          sx={{ mr: 2 }}
                          disabled={isApproving}
                          onClick={() => handleOpenApproveDialog(entry)}
                        >
                          Approve/Override
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          disabled={isApproving}
                          onClick={() => handleDeny(entry)}
                        >
                          Deny
                        </Button>
                      </Box>
                    )}
                  </Box>
                ))}

                {/* New credit operation */}
                <Box
                  sx={{
                    border: "1px dashed",
                    borderColor: "divider",
                    p: 2,
                    mt: 3,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Create New Spending Power Operation
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Operation Type</InputLabel>
                    <Select
                      value={operationType}
                      label="Operation Type"
                      onChange={(e) => setOperationType(e.target.value)}
                    >
                      <MenuItem value="init">init</MenuItem>
                      <MenuItem value="increase">increase</MenuItem>
                      <MenuItem value="decrease">decrease</MenuItem>
                      <MenuItem value="override">override</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Requested Spending Power"
                    type="number"
                    fullWidth
                    margin="normal"
                    value={requestedSpendingPower}
                    onChange={(e) => setRequestedSpendingPower(e.target.value)}
                  />
                  <TextField
                    label="Reason (optional)"
                    multiline
                    fullWidth
                    margin="normal"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    onClick={handleCreateOperation}
                    disabled={creatingSpendingOp}
                  >
                    {creatingSpendingOp ? <CircularProgress size={22} /> : "Submit Operation"}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography>
                You need to create/save the company first to view spending power history.
              </Typography>
            )}
          </TabPanel>

          {/* TEAMS TAB */}
          <TabPanel value={tabIndex} index={3}>
            {info?.id ? (
              <AdminCompanyTeams companyId={info.id} />
            ) : (
              <Typography>Save the company first, then you can manage its teams.</Typography>
            )}
          </TabPanel>

          {/* FILES TAB */}
          <TabPanel value={tabIndex} index={4}>
            {info?.id ? (
              <AdminCompanyFiles companyId={info.id} />
            ) : (
              <Typography>Save the company first to view files.</Typography>
            )}
          </TabPanel>
        </Box>

        <DialogActions
          sx={{
            borderTop: 1,
            borderColor: "divider",
            p: 2,
            mt: "auto",
            backgroundColor: "background.paper",
            position: isMobile ? "sticky" : "static",
            bottom: 0,
          }}
        >
          <Button onClick={() => onClose(false)}>Close</Button>
          {tabIndex === 0 && (
            <Button
              onClick={handleSubmitEdit}
              variant="contained"
              disabled={createLoading || editLoading}
            >
              {createLoading || editLoading ? <CircularProgress size={22} /> : "Submit"}
            </Button>
          )}
        </DialogActions>
      </Box>

      {/* Approve/Override Dialog */}
      <Dialog open={approveDialogOpen} onClose={handleCloseApproveDialog} fullWidth>
        <DialogTitle>Approve or Override Request</DialogTitle>
        <Box sx={{ p: 2 }}>
          <TextField
            label="New Spending Power"
            type="number"
            fullWidth
            sx={{ my: 2 }}
            value={overrideSpendingPower}
            onChange={(e) => setOverrideSpendingPower(e.target.value)}
          />
          <TextField
            label="New Interest Rate"
            type="number"
            fullWidth
            sx={{ mb: 2 }}
            value={overrideInterestRate}
            onChange={(e) => setOverrideInterestRate(e.target.value)}
          />
          <TextField
            label="Admin Reason"
            multiline
            fullWidth
            value={adminReason}
            onChange={(e) => setAdminReason(e.target.value)}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button onClick={handleCloseApproveDialog} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleApprove} disabled={isApproving}>
              {isApproving ? <CircularProgress size={20} /> : "Approve"}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Dialog>
  );
}